import { NetworkStatus } from '@apollo/client';
import { useMemo } from 'react';
import debounce from 'lodash/debounce';
import { InputActionMeta } from 'react-select';
import { useGetAllergenClassesQuery } from 'graphql/generated/remote-schema-hasura';

const DEBOUNCE_TIME = 600;

export const useGetAllergensClasses = () => {
  const { fetchMore, loading, data, refetch, networkStatus } =
    useGetAllergenClassesQuery({
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: { limit: 20, offset: 0 },
    });

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const medicationsList = useMemo(() => {
    const fetchedMedications = data?.getAllergenClasses?.allergenClasses ?? [];
    return fetchedMedications.map((med) => {
      return {
        value: med.classId as string,
        label: med.className,
      };
    });
  }, [data]);

  const medicationsListLabels = useMemo(() => {
    const fetchedMedications = data?.getAllergenClasses?.allergenClasses ?? [];
    return fetchedMedications.map((med) => {
      const value = med.className;
      return {
        value: value,
        label: value,
      };
    });
  }, [data]);

  const handleLoadMoreMedications = () => {
    if (loading || isFetchingMore) return;
    fetchMore({
      variables: {
        offset: medicationsList?.length || 0,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          getAllergenClasses: {
            allergenClasses: [
              ...(prev.getAllergenClasses.allergenClasses ?? []),
              ...(fetchMoreResult.getAllergenClasses.allergenClasses ?? []),
            ],
          },
        };
      },
    });
  };

  const handleSearchChange = debounce(
    (newValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change') {
        refetch({ offset: 0, search: newValue });
      }
      if (actionMeta.action === 'menu-close') {
        refetch({ offset: 0, search: '' });
      }
    },
    DEBOUNCE_TIME,
  );

  return {
    loading,
    handleLoadMoreMedications,
    handleSearchChange,
    medicationsList,
    medicationsListLabels,
    isFetchingMore,
  };
};
